<script lang="ts">
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  shallowRef,
  unref,
  vShow,
  watch,
  withDirectives
} from 'vue';

import { Intersect } from '@components/directives';
import { classNames, convertToUnit, getCurrentInstance, pick, SUPPORTS_INTERSECTION } from '@components/utils';

import {
  CdsTransition,
  makeDimensionProps,
  makeThemeProps,
  makeTransitionProps,
  provideTheme,
  useRender
} from '../../composable';

import { CdsResponsive } from './components';
import { makeImageProps, makeResponsiveProps } from './composable';

import type { ISrcObject } from './composable';

export const componentName = 'CdsImage';
export default defineComponent({
  name: componentName,
  directives: { Intersect },
  props: {
    ...makeImageProps(),
    ...makeResponsiveProps(),
    ...makeDimensionProps(),
    ...makeTransitionProps(),
    ...makeThemeProps()
  },
  emits: {
    loadstart: (value: string | undefined) => true,
    load: (value: string | undefined) => true,
    error: (value: string | undefined) => true
  },
  setup(props, { emit, slots }) {
    const vm = getCurrentInstance('CdsImage');
    const currentSrc = shallowRef('');
    const image = ref<HTMLImageElement>();
    const state = shallowRef<'idle' | 'loading' | 'loaded' | 'error'>(props.eager ? 'loading' : 'idle');
    const naturalWidth = shallowRef<number>();
    const naturalHeight = shallowRef<number>();

    const { themeClasses } = provideTheme(props);

    const normalisedSrc = computed<ISrcObject>(() => {
      return props.src && typeof props.src === 'object'
        ? {
            src: props.src.src,
            srcset: props.srcset || props.src.srcset,
            lazySrc: props.lazySrc || props.src.lazySrc,
            aspect: Number(props.aspectRatio || props.src.aspect || 0)
          }
        : {
            src: props.src,
            srcset: props.srcset,
            lazySrc: props.lazySrc,
            aspect: Number(props.aspectRatio || 0)
          };
    });

    const aspectRatio = computed(
      () => unref(normalisedSrc).aspect || unref(naturalWidth)! / unref(naturalHeight)! || 0
    );

    const containClasses = computed(() => ({
      'cds-image__img--cover': props.cover,
      'cds-image__img--contain': !props.cover
    }));

    watch(
      () => props.src,
      () => {
        init(unref(state) !== 'idle');
      }
    );

    watch(aspectRatio, (value, oldValue) => {
      if (!value && oldValue && unref(image)) {
        pollForSize(unref(image)!);
      }
    });

    onBeforeMount(() => init());

    function init(isIntersecting?: boolean) {
      if (props.eager && isIntersecting) return;
      if (SUPPORTS_INTERSECTION && !isIntersecting && !props.eager) return;

      state.value = 'loading';

      if (unref(normalisedSrc).lazySrc) {
        const lazyImage = new Image();
        lazyImage.src = unref(normalisedSrc).lazySrc!;
        pollForSize(lazyImage, null);
      }

      if (!unref(normalisedSrc).src) return;

      nextTick(() => {
        emit('loadstart', unref(image)?.currentSrc || unref(normalisedSrc).src);

        setTimeout(() => {
          if (vm.isUnmounted) return;

          if (unref(image)?.complete) {
            if (!unref(image)?.naturalWidth) onError();

            if (unref(state) === 'error') return;

            if (!unref(aspectRatio)) pollForSize(unref(image)!, null);
            if (unref(state) === 'loading') onLoad();
          } else {
            if (!unref(aspectRatio)) pollForSize(unref(image)!);
            getSrc();
          }
        });
      });
    }

    function onLoad() {
      if (vm.isUnmounted) return;

      getSrc();
      pollForSize(unref(image)!);
      state.value = 'loaded';

      emit('load', unref(image)?.currentSrc || unref(normalisedSrc)?.src);
    }

    function onError() {
      if (vm.isUnmounted) return;

      state.value = 'error';

      emit('error', unref(image)?.currentSrc || unref(normalisedSrc)?.src);
    }

    function getSrc() {
      const img = unref(image);
      if (img) currentSrc.value = img.currentSrc || img.src;
    }

    let timer = -1;

    onBeforeUnmount(() => {
      clearTimeout(timer);
    });

    function pollForSize(img: HTMLImageElement, timeout: number | null = 100) {
      const poll = () => {
        clearTimeout(timer);
        if (vm.isUnmounted) return;

        const { naturalHeight: imgHeight, naturalWidth: imgWidth } = img;

        if (imgHeight || imgWidth) {
          naturalWidth.value = imgWidth;
          naturalHeight.value = imgHeight;
        } else if (!img.complete && unref(state) === 'loading' && timeout != null) {
          timer = window.setTimeout(poll, timeout);
        } else if (img.currentSrc.endsWith('.svg') || img.currentSrc.startsWith('data:image/svg+xml')) {
          naturalWidth.value = 1;
          naturalHeight.value = 1;
        }
      };

      poll();
    }

    const __image = () => {
      if (!unref(normalisedSrc).src || unref(state) === 'idle') return null;

      const img = h('img', {
        ref: image,
        class: classNames('cds-image__img', unref(containClasses)),
        style: { objectPosition: props.position },
        src: unref(normalisedSrc).src,
        srcset: unref(normalisedSrc).srcset,
        alt: props.alt,
        crossorigin: props.crossorigin,
        referrerpolicy: props.referrerpolicy,
        draggable: props.draggable,
        sizes: props.sizes,
        onLoad,
        onError
      });

      const sources = slots.sources?.();

      return h(CdsTransition, { transition: props.transition, appear: true }, () =>
        withDirectives(sources ? h('picture', { class: 'cds-image__picture' }, [sources, img]) : img, [
          [vShow, unref(state) === 'loaded']
        ])
      );
    };

    const __preloadImage = () => {
      return h(
        CdsTransition,
        { transition: props.transition },
        () =>
          unref(normalisedSrc).lazySrc &&
          unref(state) !== 'loaded' &&
          h('img', {
            class: classNames('cds-image__img', 'cds-image__img--preload', unref(containClasses)),
            style: { objectPosition: props.position },
            src: unref(normalisedSrc).lazySrc,
            alt: props.alt,
            crossorigin: props.crossorigin,
            referrerpolicy: props.referrerpolicy,
            draggable: props.draggable
          })
      );
    };

    const __placeholder = () => {
      if (!slots.placeholder) return null;

      return h(
        CdsTransition,
        { transition: props.transition, appear: true },
        () =>
          unref(state) === 'loading' ||
          (unref(state) === 'error' &&
            !slots.error &&
            h('div', { class: classNames('cds-image__placeholder') }, slots.placeholder?.()))
      );
    };

    const __error = () => {
      if (!slots.error) return null;

      return h(
        CdsTransition,
        { transition: props.transition, appear: true },
        () => unref(state) === 'error' && h('div', { class: classNames('cds-image__error') }, slots.error?.())
      );
    };

    const __gradient = () => {
      if (!props.gradient) return null;

      return h('div', {
        class: classNames('cds-image__gradient'),
        style: { backgroundImage: `linear-gradient(${props.gradient})` }
      });
    };

    const isBooted = shallowRef(false);
    {
      const stop = watch(aspectRatio, (value) => {
        if (value) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isBooted.value = true;
            });
          });
          stop();
        }
      });
    }

    useRender(() => {
      const [responsiveProps] = pick(props, Object.keys(CdsResponsive.props) as Array<keyof typeof props>);

      return withDirectives(
        h(
          CdsResponsive,
          {
            class: classNames(
              'cds-image',
              {
                'cds-image--absolute': props.absolute,
                'cds-image--booting': !unref(isBooted)
              },
              unref(themeClasses)
            ),
            style: {
              width: convertToUnit(props.width === 'auto' ? unref(naturalWidth) : props.width)
            },
            ...responsiveProps,
            aspectRatio: unref(aspectRatio),
            'aria-label': props.alt,
            role: props.alt ? 'img' : undefined
          },
          {
            additional: () => [__image(), __preloadImage(), __placeholder(), __gradient(), __error()],
            default: slots.default
          }
        ),
        [[Intersect, { handler: init, options: props.options }]]
      );
    });

    return {
      currentSrc,
      image,
      state,
      naturalWidth,
      naturalHeight
    };
  }
});
</script>

<style lang="scss">
@import './CdsImage';
</style>
